/**
 * Data-driven replacement for the role/directorate branching that Angular
 * duplicated across defaultValues(), valueBasedOnDropdown(), handleEditCoun()
 * and GetOneCounselling().
 *
 * `resolveInitialOptions` mirrors Angular defaultValues() (login role based).
 * `resolveOptions` mirrors valueBasedOnDropdown() (selected Directorate based)
 * plus the StaffType Level1 overrides. Both are pure functions.
 *
 * The Angular `debugger` statement in valueBasedOnDropdown() is intentionally
 * NOT ported.
 */

import { Option } from './types';
import {
  COUNSELLING_TYPES_FULL,
  DIRECTORATE_LIST,
  VACANCY_LEVELS_ALL,
} from '../../../shared/services/defaultValues';

export interface RoleContext {
  userType: string;
  userType1: string;
}

export interface OptionContext extends RoleContext {
  /** selected Directorate value ('1'..'6') */
  directorate: string;
  /** selected StaffType value */
  staffType: string;
}

export interface ResolvedOptions {
  directorates: Option[];
  locations: Option[];
  counsellingTypes: Option[];
  level1: Option[];
  level2: Option[];
  vacancyLevels: Option[];
}

// ── Base option lists (Angular defaultValues literals) ──────────────────────

/** Pick a subset of DIRECTORATE_LIST by value, preserving the master order. */
const directoratesByValue = (values: string[]): Option[] =>
  DIRECTORATE_LIST.filter((o) => values.includes(o.value));

// Default directorate dropdown: DEE, DSE, SAMAGRA SHIKSHA.
const DIRECTORATES_DEFAULT: Option[] = directoratesByValue(['1', '2', '3']);

// COUNSELLING_TYPES_FULL (imported from defaultValues) is the single source of
// truth. The role/directorate specific lists below are derived from it by
// picking a subset of `value`s, so labels never duplicate or drift.

/** Pick a subset of COUNSELLING_TYPES_FULL, preserving the master order. */
const counsellingTypesByValue = (values: string[]): Option[] =>
  COUNSELLING_TYPES_FULL.filter((o) => values.includes(o.value));

// GCC: full minus Hill Up / Hill Down / Return To Home Block.
const COUNSELLING_TYPES_GCC: Option[] = counsellingTypesByValue([
  '1',
  '2',
  '3',
  '4',
  '8',
  '9',
]);

// Transfer + Promotion only.
const COUNSELLING_TYPES_TRANSFER_PROMOTION: Option[] = counsellingTypesByValue([
  '2',
  '3',
]);

/** Pick a subset of VACANCY_LEVELS_ALL by value, preserving the master order. */
const vacancyLevelsByValue = (values: string[]): Option[] =>
  VACANCY_LEVELS_ALL.filter((o) => values.includes(o.value));

// Standard vacancy levels: State, CEO, DEO (Elementary), DEO (Secondary), BEO.
const VACANCY_LEVELS_FULL: Option[] = vacancyLevelsByValue([
  '1',
  '2',
  '3',
  '4',
  '5',
]);

const LEVEL2_STANDARD: Option[] = [
  { label: 'BEO', value: '1' },
  { label: 'CEO', value: '2' },
  { label: 'DEO (Elementary)', value: '3' },
  { label: 'DEO (Secondary)', value: '4' },
];

const LOCATIONS_DEFAULT: Option[] = [
  { label: 'Within Block', value: '1' },
  { label: 'Within District', value: '2' },
  { label: 'Within State', value: '3' },
];

// ── isSCERT helper ──────────────────────────────────────────────────────────

export const isScertRole = (ctx: RoleContext): boolean =>
  ctx.userType === '5' && ctx.userType1 === '6';

/** Angular edit-mode override: handleEditCoun forces the full 9-item list. */
export const editCounsellingTypes = (): Option[] => [...COUNSELLING_TYPES_FULL];

// ── Login-based initial options (Angular defaultValues) ─────────────────────

export function resolveInitialOptions(ctx: RoleContext): ResolvedOptions {
  const { userType, userType1 } = ctx;

  // GCC login (userType1 === '29')
  if (userType1 === '29') {
    return {
      directorates: directoratesByValue(['4']),
      locations: [{ label: 'Within GCC', value: '2' }],
      counsellingTypes: COUNSELLING_TYPES_GCC,
      level1: [{ label: 'HM', value: '1' }],
      level2: [{ label: 'AEO', value: '5' }],
      vacancyLevels: vacancyLevelsByValue(['6']),
    };
  }

  // SCERT login (5/6)
  if (userType === '5' && userType1 === '6') {
    return {
      directorates: directoratesByValue(['5']),
      locations: [
        { label: 'Within District', value: '2' },
        { label: 'Within State', value: '3' },
      ],
      counsellingTypes: COUNSELLING_TYPES_TRANSFER_PROMOTION,
      level1: [{ label: 'Deputy Director / DIET Principal', value: '1' }],
      level2: [{ label: 'Joint Director', value: '1' }],
      vacancyLevels: vacancyLevelsByValue(['1', '7']),
    };
  }

  // Kallar login (5/8)
  if (userType === '5' && userType1 === '8') {
    return {
      directorates: directoratesByValue(['6']),
      locations: [{ label: 'Within State', value: '3' }],
      counsellingTypes: COUNSELLING_TYPES_FULL,
      level1: [{ label: 'AEO', value: '1' }],
      level2: [{ label: 'Joint Director', value: '1' }],
      vacancyLevels: VACANCY_LEVELS_FULL,
    };
  }

  // Default
  return {
    directorates: DIRECTORATES_DEFAULT,
    locations: LOCATIONS_DEFAULT,
    counsellingTypes: COUNSELLING_TYPES_FULL,
    level1: [{ label: 'HM', value: '1' }],
    level2: LEVEL2_STANDARD,
    vacancyLevels: VACANCY_LEVELS_FULL,
  };
}

// ── Directorate-based options (Angular valueBasedOnDropdown) ────────────────

export function resolveOptions(ctx: OptionContext): ResolvedOptions {
  const { directorate, staffType } = ctx;
  const base = resolveInitialOptions(ctx);

  let locations = base.locations;
  let counsellingTypes = base.counsellingTypes;
  let level1 = base.level1;
  let level2 = base.level2;
  let vacancyLevels = base.vacancyLevels;

  if (directorate === '1') {
    // DEE
    locations = [
      { label: 'Within Block', value: '1' },
      { label: 'Within District', value: '2' },
      { label: 'Within State', value: '3' },
      { label: 'Within Education District', value: '4' },
      { label: 'Within Revenue District', value: '5' },
    ];
    counsellingTypes = COUNSELLING_TYPES_FULL;
    level1 = [
      { label: 'BEO', value: '1' },
      { label: 'DEO (Elementary)', value: '3' },
    ];
  } else if (directorate === '2') {
    // DSE
    locations = [
      { label: 'Within District', value: '2' },
      { label: 'Within State', value: '3' },
      { label: 'Within Education District', value: '4' },
    ];
    counsellingTypes = COUNSELLING_TYPES_GCC;
    if (staffType === '26') {
      level1 = [{ label: 'DEO (Secondary)', value: '4' }];
    } else if (staffType === '27') {
      level1 = [{ label: 'CEO', value: '2' }];
    } else {
      level1 = [{ label: 'HM', value: '1' }];
    }
  } else if (directorate === '4') {
    // GCC
    locations = [{ label: 'Within GCC', value: '2' }];
    counsellingTypes = COUNSELLING_TYPES_GCC;
    level2 = [{ label: 'AEO', value: '5' }];
  } else if (directorate === '5') {
    // SCERT
    locations = [
      { label: 'Within District', value: '2' },
      { label: 'Within State', value: '3' },
    ];
    counsellingTypes = COUNSELLING_TYPES_TRANSFER_PROMOTION;
  } else if (directorate === '6') {
    // Kallar
    locations = [{ label: 'Within State', value: '3' }];
    counsellingTypes = COUNSELLING_TYPES_TRANSFER_PROMOTION;
    vacancyLevels = vacancyLevelsByValue(['1']);
    level1 = [{ label: 'AEO', value: '1' }];
    level2 = [{ label: 'Joint Director', value: '1' }];
  } else if (directorate) {
    // else (any other selected directorate, e.g. 3)
    locations = LOCATIONS_DEFAULT;
    counsellingTypes = COUNSELLING_TYPES_FULL;
  }

  // StaffType-based Level1 overrides (applied last, matching Angular order).
  if (staffType === '134') {
    level1 = [{ label: 'Joint Director', value: '2' }];
  } else if (directorate === '5') {
    level1 = [{ label: 'Deputy Director / DIET Principal', value: '1' }];
  }
  if (staffType === '103') {
    level1 = [{ label: 'CEO', value: '2' }];
  }

  return {
    directorates: base.directorates,
    locations,
    counsellingTypes,
    level1,
    level2,
    vacancyLevels,
  };
}

/** Angular GetOneCounselling(): Level2 list resolved from the loaded record's
 *  directorate/userType1. Kept separate since it runs during edit-load. */
export function resolveEditLevel2(
  ctx: RoleContext & { directorate: string; counsellingType: string }
): Option[] {
  const { userType1, directorate, counsellingType } = ctx;
  if ((counsellingType !== '9' && userType1 === '29') || directorate === '4') {
    return [{ label: 'AEO', value: '5' }];
  }
  if (directorate === '5' || directorate === '6') {
    return [{ label: 'Joint Director', value: '1' }];
  }
  return LEVEL2_STANDARD;
}
